import { BoxLeadInputKey } from "@modules/boxLead/formik/boxLeadFormik"
import { addLeadYupValidationSchema } from "@modules/boxLead/formik/boxLeadFormik"
import { useFormik } from "formik"
import { useState, useTransition } from "react"
import { BoxLeadFormStepOne } from "@modules/boxLead/components/boxLeadForm/boxLeadFormStepOne"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { BoxLeadFormStepTwo } from "@modules/boxLead/components/boxLeadForm/boxLeadFormStepTwo"
import { useToast } from "@modules/ui/components/huToast"
import { useBoxLeadCreate, useBoxLeadUpdate } from "@modules/boxLead/hooks/useBoxLeadActions"
import { BoxLeadEntity } from "@modules/boxLead/entities/boxLeadEntity"
import { BoxLeadFormHeader } from "@modules/boxLead/components/boxLeadForm/boxLeadFormHeader"
import { word } from "@modules/core/utils/i18n"
import { BoxLeadDraftEntity } from "@modules/boxLead/entities/boxLeadInput"

type BoxLeadFormLayoutProps = {
  boxLead?: BoxLeadEntity
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadFormLayout({ boxLead, className, ...props }: BoxLeadFormLayoutProps) {
  const defaultClassName = "flex flex-column gap-5"
  const [, startTransition] = useTransition()
  const { hideSidebar } = useSidebar()
  const [stepNumber, setStepNumber] = useState(1)
  const { createBoxLead } = useBoxLeadCreate()
  const { updateBoxLead } = useBoxLeadUpdate()
  const toast = useToast()

  const navigateStep = (onNext: boolean) => {
    startTransition(() => {
      setStepNumber((prev) => (onNext ? prev + 1 : prev - 1))
    })
  }

  const handleClose = () => {
    hideSidebar(false)
  }

  const formik = useFormik<BoxLeadDraftEntity>({
    initialValues: BoxLeadDraftEntity.createDraft(boxLead),
    validationSchema: addLeadYupValidationSchema(stepNumber),
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async () => {
      if (stepNumber < 2) {
        navigateStep(true)
        return
      }
      if (boxLead) {
        await updateBoxLead({
          leadId: boxLead.id,
          leadDraft: formik.values,
        })
          .then(() => {
            toast.show({
              severity: "success",
              detail: word("box.lead.form.toast.edit.success"),
            })
            hideSidebar(false)
          })
          .catch(() => {
            toast.show({
              severity: "error",
              detail: word("box.lead.form.toast.edit.error"),
            })
          })
        return
      }
      await createBoxLead(formik.values)
        .then(() => {
          toast.show({
            severity: "success",
            detail: word("box.lead.form.toast.add.success"),
          })
          hideSidebar(false)
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word("box.lead.form.toast.add.error"),
          })
        })
    },
  })

  return (
    <div className={className ?? defaultClassName} {...props}>
      <BoxLeadFormHeader isEdition={!!boxLead} />

      {stepNumber === 1 && (
        <BoxLeadFormStepOne
          handleSubmit={formik.handleSubmit}
          onPrevious={handleClose}
          isFormFieldInValid={(input: BoxLeadInputKey) => isFormFieldInValid(input, formik)}
          getFormErrorMessage={(input: BoxLeadInputKey) => getFormErrorMessage(input, formik)}
          formik={formik}
          disabled={!addLeadYupValidationSchema(stepNumber).isValidSync(formik.values)}
        />
      )}
      {stepNumber === 2 && (
        <BoxLeadFormStepTwo
          handleSubmit={formik.handleSubmit}
          onPrevious={() => navigateStep(false)}
          isFormFieldInValid={(input: BoxLeadInputKey) => isFormFieldInValid(input, formik)}
          getFormErrorMessage={(input: BoxLeadInputKey) => getFormErrorMessage(input, formik)}
          isSubmitting={formik.isSubmitting}
          formik={formik}
          disabled={!addLeadYupValidationSchema(stepNumber).isValidSync(formik.values)}
          isEdition={!!boxLead}
          user={boxLead?.user}
        />
      )}
    </div>
  )
}
